<template>
  <div class="p-6 space-y-6">
    <section class="bg-white rounded-xl shadow p-5 space-y-4">
      <h2 class="text-2xl font-bold text-gray-800">🎟️ Crear código de descuento</h2>

      <div class="grid gap-4 md:grid-cols-2">
        <div>
          <label class="block text-sm font-medium text-gray-700">Código</label>
          <input
            v-model="form.codigo"
            type="text"
            class="w-full border rounded px-3 py-2 uppercase"
            placeholder="NAVIDAD"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Tipo</label>
          <select v-model="form.tipo" class="w-full border rounded px-3 py-2">
            <option value="porcentaje">Porcentaje (%)</option>
            <option value="fijo">Monto fijo ($)</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Valor</label>
          <input
            v-model.number="form.valor"
            type="number"
            min="1"
            class="w-full border rounded px-3 py-2"
            placeholder="25"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Usos máximos (opcional)</label>
          <input
            v-model.number="form.usosMax"
            type="number"
            min="1"
            class="w-full border rounded px-3 py-2"
            placeholder="Ilimitado"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Expira el (opcional)</label>
          <input
            v-model="form.expiracion"
            type="date"
            class="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700">Descripción (opcional)</label>
          <input
            v-model="form.descripcion"
            type="text"
            class="w-full border rounded px-3 py-2"
            placeholder="Promo navideña"
          />
        </div>
      </div>

      <div class="flex gap-3">
        <button
          class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          :disabled="guardando"
          @click="guardarCupon"
        >
          {{ guardando ? 'Guardando...' : 'Guardar cupón' }}
        </button>
        <button
          class="px-4 py-2 rounded border"
          @click="limpiarFormulario"
        >
          Limpiar
        </button>
      </div>
      <p v-if="mensaje" class="text-green-600 text-sm">{{ mensaje }}</p>
      <p v-if="error" class="text-red-600 text-sm">{{ error }}</p>
    </section>

    <section class="bg-white rounded-xl shadow p-5">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-semibold text-gray-800">Cupones existentes</h3>
        <button class="text-sm text-blue-600" @click="cargarCupones">Actualizar</button>
      </div>

      <div v-if="cargandoListado" class="text-gray-500 text-sm">Cargando cupones...</div>
      <div v-else-if="!cupones.length" class="text-gray-500 text-sm">No hay cupones registrados.</div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="text-left border-b text-gray-600">
              <th class="py-2">Código</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Usos</th>
              <th>Expira</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in cupones" :key="c.id" class="border-b">
              <td class="py-2 font-semibold">{{ c.codigo }}</td>
              <td class="capitalize">{{ c.tipo }}</td>
              <td>
                <span v-if="c.tipo === 'porcentaje'">{{ c.valor }}%</span>
                <span v-else>${{ Number(c.valor).toFixed(2) }}</span>
              </td>
              <td>
                <span v-if="c.usosMax">{{ c.usosActual || 0 }} / {{ c.usosMax }}</span>
                <span v-else>Ilimitado</span>
              </td>
              <td>
                <span v-if="c.expiracion">{{ formatDate(c.expiracion) }}</span>
                <span v-else>—</span>
              </td>
              <td>
                <button
                  class="px-3 py-1 rounded text-xs"
                  :class="c.activo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'"
                  @click="toggleActivo(c)"
                >
                  {{ c.activo ? 'Activo' : 'Inactivo' }}
                </button>
              </td>
              <td>
                <button class="text-red-600 text-xs" @click="eliminarCupon(c)">Eliminar</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue"
import { db } from "@/firebase"
import {
  collection,
  getDocs,
  setDoc,
  doc,
  deleteDoc,
  updateDoc,
  Timestamp
} from "firebase/firestore"

const cupones = ref([])
const cargandoListado = ref(false)
const guardando = ref(false)
const mensaje = ref("")
const error = ref("")

const form = ref({
  codigo: "",
  tipo: "porcentaje",
  valor: 10,
  usosMax: null,
  expiracion: "",
  descripcion: ""
})

function limpiarFormulario() {
  form.value = {
    codigo: "",
    tipo: "porcentaje",
    valor: 10,
    usosMax: null,
    expiracion: "",
    descripcion: ""
  }
  error.value = ""
  mensaje.value = ""
}

function formatDate(ts) {
  try {
    const date = ts?.toDate ? ts.toDate() : ts
    if (!(date instanceof Date)) return "—"
    return date.toLocaleDateString("es-PR")
  } catch (e) {
    return "—"
  }
}

async function cargarCupones() {
  cargandoListado.value = true
  try {
    const snap = await getDocs(collection(db, "cupones"))
    cupones.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  } catch (e) {
    console.error("No se pudo cargar cupones", e)
  } finally {
    cargandoListado.value = false
  }
}

async function guardarCupon() {
  error.value = ""
  mensaje.value = ""
  const codigo = (form.value.codigo || "").trim().toUpperCase()
  const valor = Number(form.value.valor)

  if (!codigo) {
    error.value = "El código es obligatorio"
    return
  }
  if (!Number.isFinite(valor) || valor <= 0) {
    error.value = "El valor debe ser mayor a 0"
    return
  }

  guardando.value = true
  try {
    const payload = {
      codigo,
      tipo: form.value.tipo,
      valor,
      descripcion: form.value.descripcion || "",
      activo: true,
      usosMax: form.value.usosMax || null,
      usosActual: 0,
      creado: Timestamp.now(),
    }
    if (form.value.expiracion) {
      payload.expiracion = Timestamp.fromDate(new Date(form.value.expiracion))
    }

    await setDoc(doc(db, "cupones", codigo), payload, { merge: true })
    mensaje.value = "Cupón guardado"
    limpiarFormulario()
    await cargarCupones()
  } catch (e) {
    console.error("Error guardando cupón", e)
    error.value = e.message || "No se pudo guardar"
  } finally {
    guardando.value = false
  }
}

async function toggleActivo(cupon) {
  try {
    const refDoc = doc(db, "cupones", cupon.id)
    await updateDoc(refDoc, { activo: !cupon.activo })
    cupon.activo = !cupon.activo
  } catch (e) {
    console.error("No se pudo actualizar cupón", e)
  }
}

async function eliminarCupon(cupon) {
  if (!confirm(`¿Eliminar el cupón ${cupon.codigo}?`)) return
  try {
    await deleteDoc(doc(db, "cupones", cupon.id))
    cupones.value = cupones.value.filter((c) => c.id !== cupon.id)
  } catch (e) {
    console.error("No se pudo eliminar cupón", e)
  }
}

onMounted(() => {
  cargarCupones()
})
</script>
